// Division registry — must mirror DIVISIONS in backend/server.py.
// The pipeline lens only offers in_pipeline divisions (Ecotile lives in its
// own module, Phase 2), but colours/labels here serve the whole app.
export const DIVISIONS = [
  // Metallic = sea green (Baker Hughes / app green), Drilling = maroon
  // (deep Rockpecker red) — locked 14-Jul.
  { key: "metallic", label: "Metallic", color: "#0F6E56", in_pipeline: true },
  { key: "drilling", label: "Drilling", color: "#8E1F2F", in_pipeline: true },
  { key: "ecotile",  label: "Ecotile",  color: "#1D9E75", in_pipeline: false },
];

export const PIPELINE_DIVISIONS = DIVISIONS.filter(d => d.in_pipeline);

export const divisionByKey = (key) => DIVISIONS.find(d => d.key === key) || null;

export const divisionLabel = (key) => divisionByKey(key)?.label || key;
export const divisionColor = (key) => divisionByKey(key)?.color || "#94a3b8";
// Single-letter marker (M / D / E) — used by the stage-row division chips.
export const divisionInitial = (key) => (divisionLabel(key)[0] || "?").toUpperCase();

// An item with no division (shouldn't happen post-backfill) reads as metallic.
export const itemDivision = (item) => item?.division || "metallic";

// ── Per-division pipeline config (Phase 1b) ─────────────────────────────────
// Fallback mirror of DIVISION_CONFIG_SEED in backend/server.py — used until
// GET /division-config resolves (and if it ever fails), so the UI can never
// render without a config. Live values come from the DB (admin-editable).
export const DIVISION_CONFIG_FALLBACK = {
  metallic: {
    po_received: { design_approval_gate: false },
    procurement: {
      subteams: ["Material", "Tooling", "Gauging"],
      special_remark: false,
      statuses: [
        { key: "Pending", label: "Pending", locked: true },
        { key: "Ordered", label: "Ordered" },
        { key: "Partially Received", label: "Partially Received" },
        { key: "Complete", label: "Complete", terminal: true, locked: true },
      ],
    },
    manufacturing: {
      router_gate: true, kitting_gate: true,
      statuses: [
        { key: "Queued", label: "Queued", locked: true },
        { key: "Processing", label: "Processing" },
        { key: "Third Party", label: "Third Party" },
        { key: "Machining Complete", label: "Machining Complete", terminal: true, locked: true },
      ],
    },
    qc: {
      qd: true, customer_acceptance: true, coc_gate: false,
      statuses: [
        { key: "Pending Inspection", label: "Pending Inspection", locked: true },
        { key: "Under Inspection", label: "Under Inspection" },
        { key: "Passed", label: "Passed", terminal: true, locked: true },
        { key: "Failed", label: "Failed" },
        { key: "Rework Required", label: "Rework Required" },
      ],
    },
    dispatch: {
      tr_fields: true,
      statuses: [
        { key: "Pending", label: "Pending", locked: true },
        { key: "Pickup Awaited", label: "Pickup Awaited" },
        { key: "Negotiating Rates", label: "Negotiating Rates" },
        { key: "Shipped", label: "Shipped", terminal: true, locked: true },
      ],
    },
  },
  drilling: {
    po_received: { design_approval_gate: true },
    procurement: {
      subteams: ["Material", "Tooling"],
      special_remark: true,
      statuses: [
        { key: "Pending", label: "Pending", locked: true },
        { key: "Ordered", label: "New Ordered" },
        { key: "Complete", label: "Done", terminal: true, locked: true },
      ],
    },
    manufacturing: {
      router_gate: true, kitting_gate: false,
      statuses: [
        { key: "Queued", label: "Queued", locked: true },
        { key: "Machining Body", label: "Machining body" },
        { key: "Brazing", label: "Brazing" },
        { key: "Paint & Pack", label: "Paint & Pack", terminal: true, locked: true },
      ],
    },
    qc: {
      qd: false, customer_acceptance: false, coc_gate: true,
      statuses: [
        { key: "Pending Inspection", label: "Pending Inspection", locked: true },
        { key: "Passed", label: "Passed", terminal: true, locked: true },
      ],
    },
    dispatch: {
      tr_fields: false,
      statuses: [
        { key: "Pending", label: "Pending", locked: true },
        { key: "Shipped", label: "Shipped", terminal: true, locked: true },
      ],
    },
  },
};

// Config accessors — always fall back so the pipeline can never break.
export const divArea = (config, division, area) =>
  (config?.[division] || config?.metallic || DIVISION_CONFIG_FALLBACK.metallic)?.[area] || {};

export const divStatusOptions = (config, division, area) =>
  divArea(config, division, area).statuses || [];

export const divStatusLabel = (config, division, area, key) => {
  const s = divStatusOptions(config, division, area).find(x => x.key === key);
  return s ? (s.label || s.key) : key;
};

export const divTerminalKey = (config, division, area, fallback) => {
  const s = divStatusOptions(config, division, area).find(x => x.terminal);
  return s ? s.key : fallback;
};

export const divFlag = (config, division, area, flag, dflt) => {
  const v = divArea(config, division, area)[flag];
  return v === undefined ? dflt : v;
};

// Lens values: "all" | division key. Guard for localStorage restores.
export const isValidLens = (v) =>
  v === "all" || PIPELINE_DIVISIONS.some(d => d.key === v);

// A user is lens-locked when division_access is enabled with exactly one
// allowed division (e.g. dedicated drilling factory workers).
export const lockedDivisionFor = (user) => {
  if (!user || user.role === "Admin") return null;
  const da = user.permissions?.division_access;
  if (!da?.enabled) return null;
  const allowed = (da.allowed_divisions || []).filter(isValidLensKeySafe);
  return allowed.length === 1 ? allowed[0] : null;
};

function isValidLensKeySafe(k) {
  return DIVISIONS.some(d => d.key === k);
}
